import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Globe, Volume2, Loader2, Star, CheckCircle,
  Play, RefreshCw, Book, Music, Sparkles, Flag, Heart, Languages
} from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const LanguageLearning = () => {
  const { childId, language } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [child, setChild] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [staticLessons, setStaticLessons] = useState([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [phase, setPhase] = useState('overview'); // overview, learning, practice, cultural

  const languageConfig = {
    spanish: {
      name: 'Spanish',
      nativeName: 'Español',
      flag: '🇪🇸',
      colors: 'from-red-500 to-yellow-500',
      greeting: '¡Hola!',
      bgColor: 'bg-gradient-to-br from-red-50 to-yellow-50'
    },
    french: {
      name: 'French',
      nativeName: 'Français',
      flag: '🇫🇷',
      colors: 'from-blue-500 to-red-500',
      greeting: 'Bonjour!',
      bgColor: 'bg-gradient-to-br from-blue-50 to-red-50'
    },
    akan: {
      name: 'Akan',
      nativeName: 'Twi & Fante',
      flag: '🇬🇭',
      colors: 'from-green-500 to-yellow-500',
      greeting: 'Maakye!',
      bgColor: 'bg-gradient-to-br from-green-50 to-yellow-50'
    }
  };

  const config = languageConfig[language] || languageConfig.spanish;

  useEffect(() => {
    fetchData();
  }, [childId, language]);

  const fetchData = async () => {
    try {
      const [childRes, lessonsRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/languages/${language}/lessons/1`)
      ]);
      
      setChild(childRes.data);
      setStaticLessons(lessonsRes.data.lessons || []);
      
      // Generate AI lesson
      await generateAILesson();
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load language lessons');
    } finally {
      setLoading(false);
    }
  };

  const generateAILesson = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/languages/${language}/ai-lesson/${childId}`);
      setLesson(response.data.lesson);
      
      if (response.data.source === 'ai_generated') {
        toast.success('AI created a personalized lesson!', {
          icon: <Sparkles className="w-4 h-4 text-amber-500" />
        });
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Complete Digital Safety training first');
        navigate(`/digital-safety/${childId}`);
      } else {
        console.error('Failed to generate lesson:', error);
      }
    } finally {
      setGenerating(false);
    }
  };

  const speakWord = (text, lang = language) => {
    _speakTTS(text, 0.8);
  };

  const nextWord = () => {
    const words = lesson?.vocabulary || [];
    if (currentWordIndex < words.length - 1) {
      setCurrentWordIndex(prev => prev + 1);
      setShowTranslation(false);
    }
  };

  const prevWord = () => {
    if (currentWordIndex > 0) {
      setCurrentWordIndex(prev => prev - 1);
      setShowTranslation(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading {config.name} lessons...</p>
        </div>
      </div>
    );
  }

  const currentWord = lesson?.vocabulary?.[currentWordIndex];

  return (
    <div className={`min-h-screen ${config.bgColor}`}>
      {/* Header */}
      <header className={`bg-gradient-to-r ${config.colors} text-white py-4 px-4 sticky top-0 z-40`}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to={`/learn/${childId}`} data-testid="back-to-learning">
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          
          <div className="flex items-center gap-3">
            <div className="text-4xl">{config.flag}</div>
            <div>
              <h1 className="font-bold text-lg">{config.name}</h1>
              <p className="text-xs text-white/80">{config.nativeName}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Badge className="bg-white/20 text-white border-0">
              Level {child?.language_levels?.[language] || 1}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <Tabs value={phase} onValueChange={setPhase} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 bg-white shadow-md">
            <TabsTrigger value="overview" data-testid="overview-tab">
              <Globe className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="learning" data-testid="learning-tab">
              <Book className="w-4 h-4 mr-2" />
              Learn
            </TabsTrigger>
            <TabsTrigger value="practice" data-testid="practice-tab">
              <Play className="w-4 h-4 mr-2" />
              Practice
            </TabsTrigger>
            <TabsTrigger value="cultural" data-testid="cultural-tab">
              <Heart className="w-4 h-4 mr-2" />
              Culture
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            <Card className="border-0 shadow-xl overflow-hidden">
              <div className={`bg-gradient-to-r ${config.colors} p-8 text-white text-center`}>
                <div className="text-6xl mb-4">{config.flag}</div>
                <h2 className="text-3xl font-bold mb-2">{config.greeting}</h2>
                <p className="text-white/80">Welcome to {config.name} learning!</p>
              </div>
              
              <CardContent className="p-6">
                {generating ? (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-pulse" />
                    <p className="text-slate-600">AI is creating your personalized lesson...</p>
                  </div>
                ) : lesson ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-800">{lesson.title}</h3>
                        <p className="text-slate-500">Theme: {lesson.theme}</p>
                      </div>
                      <Badge className={`bg-gradient-to-r ${config.colors} text-white border-0`}>
                        AI Generated
                      </Badge>
                    </div>
                    
                    <p className="text-slate-600">{lesson.introduction}</p>
                    
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        onClick={generateAILesson}
                        disabled={generating}
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                        New Lesson
                      </Button>
                      <Button
                        onClick={() => setPhase('learning')}
                        className={`bg-gradient-to-r ${config.colors}`}
                        data-testid="start-learning-btn"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Learning
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No lesson available</p>
                )}
              </CardContent>
            </Card>

            {/* Static Lessons */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Available Lessons</CardTitle>
                <CardDescription>Browse all {config.name} lessons</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3">
                  {staticLessons.map((lesson, idx) => (
                    <div 
                      key={lesson.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 bg-gradient-to-r ${config.colors} rounded-xl flex items-center justify-center text-white font-bold`}>
                          {idx + 1}
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-800">{lesson.title}</h4>
                          <p className="text-sm text-slate-500">{lesson.words?.length || 0} words</p>
                        </div>
                      </div>
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LEARNING TAB */}
          <TabsContent value="learning" className="space-y-6">
            {lesson?.vocabulary?.length > 0 && (
              <>
                {/* Progress */}
                <Card className="border-0 shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-500">
                        Word {currentWordIndex + 1} of {lesson.vocabulary.length}
                      </span>
                    </div>
                    <Progress 
                      value={((currentWordIndex + 1) / lesson.vocabulary.length) * 100} 
                      className="h-2"
                    />
                  </CardContent>
                </Card>

                {/* Word Card */}
                <motion.div
                  key={currentWordIndex}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="border-0 shadow-xl overflow-hidden" data-testid="word-card">
                    <div className={`bg-gradient-to-r ${config.colors} p-8 text-white text-center`}>
                      <h2 className="text-4xl font-bold mb-2">{currentWord?.word}</h2>
                      {currentWord?.pronunciation && (
                        <p className="text-white/80 text-lg">/{currentWord.pronunciation}/</p>
                      )}
                      <Button
                        variant="ghost"
                        size="lg"
                        onClick={() => speakWord(currentWord?.word)}
                        className="mt-4 text-white hover:bg-white/20"
                        data-testid="speak-word-btn"
                      >
                        <Volume2 className="w-6 h-6 mr-2" />
                        Listen
                      </Button>
                    </div>
                    
                    <CardContent className="p-6 text-center">
                      <AnimatePresence mode="wait">
                        {showTranslation ? (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                          >
                            <p className="text-2xl font-semibold text-slate-800 mb-2">
                              {currentWord?.translation}
                            </p>
                            {currentWord?.example && (
                              <p className="text-slate-600 italic">
                                "{currentWord.example}"
                              </p>
                            )}
                          </motion.div>
                        ) : (
                          <Button
                            onClick={() => setShowTranslation(true)}
                            variant="outline"
                            size="lg"
                            className="text-lg"
                            data-testid="show-translation-btn"
                          >
                            Tap to see translation
                          </Button>
                        )}
                      </AnimatePresence>
                    </CardContent>
                  </Card>
                </motion.div>

                {/* Navigation */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={prevWord}
                    disabled={currentWordIndex === 0}
                    className="flex-1"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                  <Button
                    onClick={nextWord}
                    disabled={currentWordIndex >= lesson.vocabulary.length - 1}
                    className={`flex-1 bg-gradient-to-r ${config.colors}`}
                    data-testid="next-word-btn"
                  >
                    Next Word
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </TabsContent>

          {/* PRACTICE TAB */}
          <TabsContent value="practice" className="space-y-6">
            {lesson?.practice_activity && (
              <Card className="border-0 shadow-xl">
                <CardHeader className={`bg-gradient-to-r ${config.colors} text-white`}>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Practice Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-slate-600 mb-4">{lesson.practice_activity.instructions}</p>
                  
                  <div className="grid gap-3">
                    {lesson.practice_activity.items?.map((item, idx) => (
                      <div 
                        key={idx}
                        className="p-4 bg-slate-50 rounded-xl text-center font-medium text-slate-700 hover:bg-slate-100 cursor-pointer transition-colors"
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Song/Rhyme */}
            {lesson?.song_or_rhyme && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Music className="w-5 h-5 text-pink-500" />
                    {lesson.song_or_rhyme.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 whitespace-pre-line leading-relaxed">
                    {lesson.song_or_rhyme.lyrics}
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => speakWord(lesson.song_or_rhyme.lyrics)}
                  >
                    <Volume2 className="w-4 h-4 mr-2" />
                    Listen to Song
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* CULTURAL TAB */}
          <TabsContent value="cultural" className="space-y-6">
            {lesson?.cultural_fun_fact && (
              <Card className="border-0 shadow-xl overflow-hidden">
                <div className={`bg-gradient-to-r ${config.colors} p-6 text-white`}>
                  <div className="flex items-center gap-3">
                    <div className="text-5xl">{config.flag}</div>
                    <div>
                      <h3 className="font-bold text-xl">Cultural Fun Fact!</h3>
                      <p className="text-white/80">Learn about {config.name}-speaking cultures</p>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-slate-700 text-lg leading-relaxed">
                    {lesson.cultural_fun_fact}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Language Tips */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Learning Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <span>Practice speaking out loud every day!</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <span>Watch cartoons or videos in {config.name}</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <span>Learn songs - they help you remember words!</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
                    <span>Don't be afraid to make mistakes!</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default LanguageLearning;
